import classnames from '../../node_modules/classnames/index.js';
import { forwardRef, useState, useCallback, useMemo, useRef, useLayoutEffect, useImperativeHandle, createElement } from 'react';
import { escapeRegExp } from '../util/index.js';
import { __spreadArray, __assign } from '../../node_modules/tslib/tslib.es6.js';
import { useEffectOnUpdate } from '../util/hooks.js';
import { getIndices } from '../util/highlight.js';
import ListItemComponent from './ListItem.js';
import useListKeyboardInteractions from './useListKeyboardInteractions.js';
import Input from './Input.js';
import { alignNearest } from '../util/scroll.js';
import styles from '../styles/List.module.scss';

var createHighlightFn = function (highlight, queryPattern, highlightByDefault) {
    if (highlightByDefault === void 0) { highlightByDefault = false; }
    return function (item, text, query) {
        // User-supplied highlight function
        return typeof highlight === "function"
            ? highlight(item, query)
            : // Regular expression
                typeof highlight === "object"
                    ? getIndices(text !== null && text !== void 0 ? text : "", highlight)
                    : // Always highlight
                        highlight === true
                            ? text
                                ? [[0, text.length - 1]]
                                : []
                            : // Never highlight
                                highlight === false
                                    ? []
                                    : // Highlight search query
                                        queryPattern
                                            ? getIndices(text !== null && text !== void 0 ? text : "", queryPattern)
                                            : // Highlight by default
                                                highlightByDefault && text
                                                    ? [[0, text.length - 1]]
                                                    : [];
    };
};
var getDeps = function (input) {
    // React requires the dependency list to be an array of constant length.
    return typeof input === "object" ? [input.flags, input.source] : [input, input];
};
/**
 * Returns a range from start (inclusive) to end (exclusive).
 * Similar to https://underscorejs.org/#range.
 */
var getRange = function (start, end) {
    return new Array(end - start).fill(null).map(function (_, i) { return start + i; });
};
/**
 * An interactive list in which one or more elements can be selected.
 */
var List = function (_a, ref) {
    var _b;
    var className = _a.className, disabled = _a.disabled, displayQuery = _a.displayQuery, highlightDescription = _a.highlightDescription, highlightText = _a.highlightText, inPopover = _a.inPopover, initialItems = _a.initialItems, items = _a.items, match = _a.match, multiple = _a.multiple, onChange = _a.onChange, onQueryChange = _a.onQueryChange, _c = _a.query, query = _c === void 0 ? "" : _c, scrollable = _a.scrollable, searchPlaceholder = _a.searchPlaceholder, selectedIndices = _a.selectedIndices, showSearch = _a.showSearch, style = _a.style;
    var _d = useState(), prevQuery = _d[0], setPrevQuery = _d[1];
    var _e = useState(initialItems), _shownItems = _e[0], setShownItems = _e[1];
    // Reset the number of shown items if the query changes.
    if (query !== prevQuery) {
        setPrevQuery(query);
        setShownItems(initialItems);
    }
    var queryPattern = query ? new RegExp(escapeRegExp(query), "i") : undefined;
    var defaultMatchFn = useCallback(function (item) {
        return !queryPattern ||
            (item.description ? queryPattern.test(item.description) : false) ||
            queryPattern.test(item.text);
    }, getDeps(queryPattern));
    var getDescriptionHighlights = useCallback(createHighlightFn(highlightDescription, queryPattern), __spreadArray(__spreadArray([], getDeps(highlightDescription)), getDeps(queryPattern)));
    var getHighlights = useCallback(createHighlightFn(highlightText, queryPattern, true), __spreadArray(__spreadArray([], getDeps(highlightText)), getDeps(queryPattern)));
    var shownItems = Math.min(_shownItems !== null && _shownItems !== void 0 ? _shownItems : items.length, items.length);
    var filteredItems = useMemo(function () {
        return items
            // Map items before filtering to preserve original indices.
            .map(function (item, index) { return ({ index: index, item: item }); })
            .filter(function (_a) {
            var item = _a.item;
            return (match !== null && match !== void 0 ? match : defaultMatchFn)(item, query);
        })
            .slice(0, shownItems)
            .map(function (data) { return (__assign(__assign({}, data), { descriptionHighlights: getDescriptionHighlights(data.item, data.item.description, query), highlights: getHighlights(data.item, data.item.text, query) })); });
    }, [
        defaultMatchFn,
        getDescriptionHighlights,
        getHighlights,
        items,
        match,
        query,
        shownItems,
    ]);
    // References to the currently rendered list items. Each index
    // in the filteredItems array maps to an element.
    var itemRefs = useRef([]);
    var listRef = useRef(null);
    var searchRef = useRef(null);
    var raiseChange = function (change, newValues) {
        if (multiple) {
            onChange === null || onChange === void 0 ? void 0 : onChange({
                selectedIndices: change === "add"
                    ? __spreadArray(__spreadArray([], selectedIndices), newValues.filter(function (i) { return !selectedIndices.includes(i); })) : change === "remove"
                    ? selectedIndices.filter(function (i) { return !newValues.includes(i); })
                    : newValues,
            });
        }
        else {
            onChange === null || onChange === void 0 ? void 0 : onChange({
                selectedIndices: change === "remove" ? [] : [newValues[0]],
            });
        }
    };
    var _f = useListKeyboardInteractions({
        filteredItems: filteredItems,
        itemRefs: itemRefs,
        multiple: multiple !== null && multiple !== void 0 ? multiple : false,
        onChange: raiseChange,
        selectedIndices: selectedIndices,
    }), getFocusableIndex = _f.getFocusableIndex, getTabFocusIndex = _f.getTabFocusIndex, handleKeyDown = _f.handleKeyDown, handleKeyUp = _f.handleKeyUp, rangeAnchorRef = _f.rangeAnchorRef, setTabFocusIndex = _f.setTabFocusIndex;
    var handleSearchKeyDown = function (e) {
        var _a, _b;
        if (e.key === "ArrowDown") {
            // Shift focus to the first item in the list. This is an alternative
            // to using the tab key which will focus the selected item in the list,
            // if any. By having the down key focus the first item the user can
            // keep pressing it to move focus through all list items.
            (_b = (_a = itemRefs.current) === null || _a === void 0 ? void 0 : _a[getFocusableIndex("first")]) === null || _b === void 0 ? void 0 : _b.focus();
            e.preventDefault();
        }
    };
    var handleToggle = function (index, range) {
        // Handle Shift+Click selections.
        if (multiple && range && rangeAnchorRef.current !== -1) {
            var a = indexToFiltered(rangeAnchorRef.current);
            var i = indexToFiltered(index);
            // Ensure the range anchor item hasn't been removed
            // from the list since it was marked.
            if (a > -1) {
                raiseChange("replace", (i > a ? getRange(a, i + 1) : getRange(i, a + 1))
                    // Skip over disabled items in the range.
                    .map(function (i) {
                    return filteredItems[i].item.disabled ? -1 : filteredItems[i].index;
                })
                    .filter(function (i) { return i > -1; }));
                return;
            }
        }
        raiseChange(selectedIndices.includes(index) ? "remove" : "add", [index]);
    };
    /** Maps an index in the original items array to an index in the array of filtered items. */
    var indexToFiltered = function (index) {
        return filteredItems.findIndex(function (_a) {
            var i = _a.index;
            return index === i;
        });
    };
    // Load more items if the last item is scrolled into view.
    useLayoutEffect(function () {
        var lastItemRef = itemRefs.current[shownItems - 1];
        if (initialItems !== undefined && lastItemRef) {
            var observer_1 = new IntersectionObserver(function (_a) {
                var entry = _a[0];
                return entry.isIntersecting &&
                    setShownItems(function (value) { return (value !== null && value !== void 0 ? value : 0) + initialItems; });
            }, {
                root: listRef.current,
            });
            observer_1.observe(lastItemRef);
            return function () {
                observer_1.disconnect();
            };
        }
    }, [initialItems, query, shownItems]);
    useEffectOnUpdate(function () {
        var _a;
        (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo(0, 0);
    }, [query]);
    useImperativeHandle(ref, function () { return ({
        focus: function () {
            var lastFocusedItem = itemRefs.current[getTabFocusIndex()];
            // Focus on the search input if that's available,
            // or otherwise on the last focused list item.
            if (searchRef.current) {
                searchRef.current.focus();
                // Scroll the last focused list item into view,
                // even if focus is currently on the search input.
                if (lastFocusedItem && listRef.current) {
                    var frame = listRef.current.getBoundingClientRect();
                    var target = lastFocusedItem.getBoundingClientRect();
                    listRef.current.scrollTop = alignNearest(frame.top, frame.bottom, frame.height, 0, 0, target.top, target.bottom, target.height);
                }
            }
            else {
                lastFocusedItem === null || lastFocusedItem === void 0 ? void 0 : lastFocusedItem.focus();
            }
        },
        getSearchEl: function () { return searchRef.current; },
    }); });
    if (filteredItems.length === 0 && !showSearch) {
        throw Error("A List must have at least one item or search field.");
    }
    if (selectedIndices.length > 1 && !multiple) {
        throw Error("A single-select List may not have multiple selected indices.");
    }
    return (createElement("div", { className: classnames(styles.wrap, (_b = {},
            _b[styles.bordered] = !inPopover && (scrollable || showSearch),
            _b[styles.scrollable] = scrollable,
            _b), className), style: style },
        showSearch ? (createElement(Input, { className: styles["search" + (filteredItems.length === 0 ? "-no-results" : "")], disabled: disabled, onChange: function (e) { return onQueryChange === null || onQueryChange === void 0 ? void 0 : onQueryChange({ value: e.target.value }); }, onKeyDown: handleSearchKeyDown, placeholder: searchPlaceholder, ref: searchRef, type: "search", value: displayQuery !== null && displayQuery !== void 0 ? displayQuery : query })) : null,
        createElement("ul", { "aria-disabled": disabled === true, "aria-multiselectable": multiple === true, className: styles.list, ref: listRef, role: "listbox" }, filteredItems.map(function (_a, mappedIndex) {
            var descriptionHighlights = _a.descriptionHighlights, highlights = _a.highlights, i = _a.index, itemProps = _a.item;
            return (createElement(ListItemComponent, __assign({}, itemProps, { disabled: disabled || itemProps.disabled, highlightDescription: descriptionHighlights, highlightText: highlights, key: i, noBorder: inPopover || scrollable || showSearch, onClick: function (e) { return handleToggle(i, e.shiftKey); }, onFocus: function () { return setTabFocusIndex(i); }, onKeyDown: function (e) { return handleKeyDown(i, e); }, onKeyUp: function (e) { return handleKeyUp(i, e); }, onToggle: function () { return handleToggle(i, false); }, ref: function (el) { return (itemRefs.current[mappedIndex] = el); }, selected: selectedIndices.includes(i), selectedStyle: multiple ? "checkbox" : undefined, 
                // Uses the roving tabindex pattern from https://developer.mozilla.org/en-US/docs/Web/Accessibility/Keyboard-navigable_JavaScript_widgets#managing_focus_inside_groups.
                tabIndex: i === getTabFocusIndex() && !disabled ? 0 : -1 })));
        }))));
};
var List$1 = forwardRef(List);

export default List$1;
